import {
  DatePicker as KeyboardDatePickerMui,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Fragment,
} from 'react';
import { cloneDeep } from 'lodash';
import TextField from '@mui/material/TextField';
import styled from 'styled-components';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { RadioGroup } from './RadioGroup';
import { SectionBase } from './SectionBase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';

const OTHER = ['Other'];

const RENDERS_PERIOD = ['Custom period', 'Other'];

const RENDERS_DATE = ['Calendar Year: Other', 'Fiscal Year: Other'];

export const SectionDates = ({
  title,
  subTitle,
  helpText,
  options: options0,
  questionId,
  value: originalValue,
  onChange,
  error,
  otherType,
  guidanceText,
}) => {
  const options = useMemo(() => options0?.map(response => response), [
    options0,
  ]);
  const [text, setText] = useState('');
  const [value, setValue] = useState(cloneDeep(originalValue));

  const parseValue = sentValue => {
    const copyValue = cloneDeep(sentValue);
    if (copyValue.option.toLowerCase().includes('other')) {
      copyValue.option = 'Other';
      if (typeof copyValue.startDate === 'string') {
        copyValue.startDate = new Date(copyValue.startDate + 'T00:00:00');
      }
      if (typeof copyValue.endDate === 'string') {
        copyValue.endDate = new Date(copyValue.endDate + 'T00:00:00');
      }
      setValue(copyValue);
    }
    if (copyValue.option.toLowerCase().includes('this program')) {
      copyValue.option = options?.[0];
      copyValue.startDate = null;
      copyValue.endDate = null;
      setValue(copyValue);
    }
    if (copyValue.option.toLowerCase().includes('next program')) {
      copyValue.option = options?.[1];
      copyValue.startDate = null;
      copyValue.endDate = null;
      setValue(copyValue);
    }
  };

  useEffect(() => {
    if (originalValue?.option) {
      parseValue(originalValue);
    }
  }, []);

  useEffect(() => {
    if (originalValue?.option) {
      parseValue(originalValue);
    }
  }, [originalValue]);

  const handleOption = useCallback(
    event => {
      onChange(questionId, {
        ...value,
        option: event.target.value,
        startDate: null,
        endDate: null,
      });
    },
    [questionId, value, onChange],
  );

  const handleStartDate = useCallback(
    startDate => {
      onChange(questionId, {
        ...value,
        startDate,
      });
    },
    [questionId, value, onChange],
  );

  const handleEndDate = useCallback(
    endDate => {
      onChange(questionId, {
        ...value,
        endDate,
      });
    },
    [questionId, value, onChange],
  );

  // useEffect(() => {
  //   const startDate = new Date()
  //   const endDate = new Date(startDate.getTime() + 86400000)
  //   onChange(questionId, {
  //     ...value,
  //     startDate,
  //     endDate,
  //   })
  //   /* do not include value on dependencies as values is mutated within and produces an infinite loop */
  // }, [questionId]) // eslint-disable-line

  const handleWrite = useCallback(
    ev => {
      setText(ev.target.value);
      onChange(questionId, {
        ...value,
        custom: event.target.value, // eslint-disable-line
      });
    },
    [questionId, value, onChange],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <SectionBase
        title={title}
        subTitle={subTitle}
        helpText={helpText}
        error={error}
        questionId={questionId}
        guidanceText={guidanceText}
      >
        {options && (
          <RadioGroup
            {...{ subTitle, options }}
            value={value?.option}
            onChange={handleOption}
          />
        )}
        {/* Only shows datepicker if custom period is selected or there are no options, eg, is just a PERIOD */
        otherType !== 'short_text' &&
          (RENDERS_PERIOD.indexOf(value?.option) > -1 ||
            !options ||
            options.length === 0) && (
            <DateContainer>
              <div>
                <TrueImpactText variant="body-bold">Start date:</TrueImpactText>
                <KeyboardDatePicker
                  variant="inline"
                  format={'dd MMM yyyy'}
                  // emptyLabel='Select a start date'
                  placeholder="Select a start date"
                  value={value?.startDate}
                  onChange={handleStartDate}
                  keyboardIcon={<FontAwesomeIcon icon={faCalendar} />}
                />
              </div>
              <div>
                <TrueImpactText variant="body-bold">End date:</TrueImpactText>
                <KeyboardDatePicker
                  variant="inline"
                  format={'dd MMM yyyy'}
                  placeholder="Select a end date"
                  value={value?.endDate}
                  onChange={handleEndDate}
                  keyboardIcon={<FontAwesomeIcon icon={faCalendar} />}
                />
              </div>
            </DateContainer>
          )}
        {/* Only shows single date */
        otherType !== 'short_text' && RENDERS_DATE.indexOf(value?.option) > -1 && (
          <DateContainer>
            <div>
              <TrueImpactText variant="body-bold">Date:</TrueImpactText>
              <KeyboardDatePicker
                variant="inline"
                format={'dd MMM yyyy'}
                // emptyLabel='Select a start date'
                placeholder="Select a start date"
                value={value?.startDate}
                onChange={handleStartDate}
                keyboardIcon={<FontAwesomeIcon icon={faCalendar} />}
              />
            </div>
          </DateContainer>
        )}
        {/* Only shows single date */
        otherType === 'short_text' && OTHER.indexOf(value?.option) > -1 && (
          <Fragment>
            <InputHelp>
              <span>Describe the issue:</span>
              <span>(Optional)</span>
            </InputHelp>
            <Input value={text} onChange={handleWrite} />
          </Fragment>
        )}
      </SectionBase>
    </LocalizationProvider>
  );
};

const KeyboardDatePickerInner = props => (
  <KeyboardDatePickerMui
    disablePast
    inputFormat="dd MMM yyyy"
    renderInput={params => (
      <TextField
        {...params}
        margin="normal"
        fullWidth
        placeholder={props.placeholder}
      />
    )}
    {...props}
  />
);

export const KeyboardDatePicker = styled(KeyboardDatePickerInner)`
  width: 100%;
  background-color: #ffffff !important;
`;

export const DateContainer = styled.div`
  display: flex;
  gap: ${SPACERS.medium};
  margin-bottom: 30px;
`;

const Input = styled(TextField).attrs({
  fullWidth: true,
  multiline: true,
  rows: 5,
})`
  background-color: #ffffff !important;
  .Mui-disabled {
    background-color: #f1f1f1 !important;
  }
`;

const InputHelp = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  span {
    color: #566778;
    font-size: 14px;
    :last-of-type {
      font-size: 12px;
      font-style: italic;
    }
  }
`;
